'use client';

/**
 * MatchCard Component
 *
 * Displays a match candidate with score, field comparison, and action buttons.
 * Part of the identity resolution component suite.
 */

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { motion } from 'framer-motion';
import type React from 'react';
import { forwardRef, useCallback, useMemo, useState } from 'react';
import { getNeonStyles, type NeonAccent } from '../../theme/palette';
import { createNeonGlow, glassEffects } from '../../theme/shadows';
import { interactionAnimations, motionPresets, transitionPresets } from '../../theme/transitions';
import type { LinkType, MatchCardProps } from '../../types/identityResolution';
import { FieldComparison } from './FieldComparison';
import { MatchScore } from './MatchScore';

// ============================================================================
// Constants
// ============================================================================

const LINK_TYPE_OPTIONS: {
  value: LinkType;
  label: string;
  description: string;
}[] = [
  {
    value: 'DUPLICATE',
    label: 'Duplicate',
    description: 'These records are the same entity',
  },
  {
    value: 'ALIAS',
    label: 'Alias',
    description: 'Different name for the same entity',
  },
  {
    value: 'RELATED',
    label: 'Related',
    description: 'Records are related but distinct',
  },
  {
    value: 'HIERARCHY',
    label: 'Parent/Child',
    description: 'Hierarchical relationship',
  },
];

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCardProps {
  $isDark: boolean;
  $neonAccent?: NeonAccent;
  $expanded: boolean;
}

const StyledCard = styled(motion.div, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledCardProps>(({ $isDark, $neonAccent }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;
  const neonStyles = $neonAccent ? getNeonStyles($neonAccent, $isDark) : null;

  return {
    position: 'relative',
    borderRadius: 16,
    overflow: 'hidden',
    transition: transitionPresets.card,

    // Use neon styling if accent is set, otherwise use glass styling
    ...(neonStyles
      ? {
          background: neonStyles.background,
          border: `1px solid ${neonStyles.border}`,
          boxShadow: createNeonGlow($neonAccent!, 'subtle'),

          '&:hover': {
            boxShadow: createNeonGlow($neonAccent!, 'medium'),
          },
        }
      : {
          background: glass.background,
          backdropFilter: glass.backdropBlur,
          border: `1px solid ${glass.border}`,
          boxShadow: glass.shadow,

          '&:hover': {
            background: glass.backgroundHover,
            border: `1px solid ${glass.borderHover}`,
            boxShadow: glass.shadowHover,
          },
        }),
  };
});

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(2.5),
  gap: theme.spacing(2),
}));

const StyledContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(0, 2.5, 2.5),
}));

const StyledExpandButton = styled(IconButton, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<{ $expanded: boolean }>(({ theme, $expanded }) => ({
  width: 32,
  height: 32,
  transition: transitionPresets.all,
  transform: $expanded ? 'rotate(180deg)' : 'rotate(0deg)',
  color: theme.palette.text.secondary,

  '&:hover': {
    background: theme.palette.action.hover,
  },
}));

interface StyledActionButtonProps {
  $variant: 'confirm' | 'reject' | 'ignore';
  $isDark: boolean;
}

const StyledActionButton = styled(motion.button, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledActionButtonProps>(({ theme, $variant, $isDark }) => {
  const getColors = () => {
    switch ($variant) {
      case 'confirm':
        return {
          bg: $isDark ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.1)',
          border: $isDark ? 'rgba(16, 185, 129, 0.3)' : 'rgba(16, 185, 129, 0.25)',
          text: '#10b981',
          hoverBg: $isDark ? 'rgba(16, 185, 129, 0.25)' : 'rgba(16, 185, 129, 0.15)',
        };
      case 'reject':
        return {
          bg: $isDark ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
          border: $isDark ? 'rgba(239, 68, 68, 0.3)' : 'rgba(239, 68, 68, 0.25)',
          text: '#ef4444',
          hoverBg: $isDark ? 'rgba(239, 68, 68, 0.25)' : 'rgba(239, 68, 68, 0.15)',
        };
      case 'ignore':
      default:
        return {
          bg: $isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)',
          border: $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          text: theme.palette.text.secondary,
          hoverBg: $isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)',
        };
    }
  };

  const colors = getColors();

  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '10px 20px',
    borderRadius: 10,
    border: `1px solid ${colors.border}`,
    background: colors.bg,
    color: colors.text,
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: transitionPresets.button,
    fontFamily: theme.typography.fontFamily,

    '&:hover': {
      background: colors.hoverBg,
    },

    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  };
});

const StyledRecordInfo = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: 0,

  '& .record-title': {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: 4,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },

  '& .record-meta': {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
}));

const StyledRuleBadge = styled(Chip)(() => ({
  height: 22,
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '0.02em',
}));

// ============================================================================
// Component
// ============================================================================

export const MatchCard = forwardRef<HTMLDivElement, MatchCardProps>(
  (
    {
      match,
      sourceRecord,
      fields,
      onConfirm,
      onReject,
      onIgnore,
      showDetails = true,
      expandable = true,
      disabled = false,
      neonAccent,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [expanded, setExpanded] = useState(!expandable);
    const [loading, setLoading] = useState<'confirm' | 'reject' | 'ignore' | null>(null);
    const [linkMenuAnchor, setLinkMenuAnchor] = useState<null | HTMLElement>(null);

    // Get a display title for the match record
    const recordTitle = useMemo(() => {
      const data = match.record.data;
      // Try common name fields
      for (const key of ['name', 'title', 'displayName', 'fullName', 'label']) {
        if (data[key]) return String(data[key]);
      }
      // Fallback to ID
      return `Record #${match.record.id}`;
    }, [match.record]);

    // Build field score map for quick lookup
    const fieldScoreMap = useMemo(() => {
      const map = new Map<string, number>();
      for (const fs of match.fieldScores) {
        map.set(fs.field, fs.score);
      }
      return map;
    }, [match.fieldScores]);

    // Handle confirm with link type
    const handleConfirmClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      setLinkMenuAnchor(event.currentTarget);
    }, []);

    const handleLinkTypeSelect = useCallback(
      async (linkType: LinkType) => {
        setLinkMenuAnchor(null);
        if (!onConfirm || disabled) return;

        setLoading('confirm');
        try {
          await onConfirm(linkType);
        } finally {
          setLoading(null);
        }
      },
      [onConfirm, disabled],
    );

    const handleReject = useCallback(async () => {
      if (!onReject || disabled) return;

      setLoading('reject');
      try {
        await onReject();
      } finally {
        setLoading(null);
      }
    }, [onReject, disabled]);

    const handleIgnore = useCallback(async () => {
      if (!onIgnore || disabled) return;

      setLoading('ignore');
      try {
        await onIgnore();
      } finally {
        setLoading(null);
      }
    }, [onIgnore, disabled]);

    const toggleExpanded = useCallback(() => {
      if (expandable) {
        setExpanded((prev) => !prev);
      }
    }, [expandable]);

    return (
      <StyledCard
        ref={ref}
        $isDark={isDark}
        $neonAccent={neonAccent}
        $expanded={expanded}
        initial={motionPresets.fadeInUp.initial}
        animate={motionPresets.fadeInUp.animate}
        transition={motionPresets.fadeInUp.transition}
        whileHover={interactionAnimations.cardHover}
      >
        {/* Header */}
        <StyledHeader>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              flex: 1,
              minWidth: 0,
            }}
          >
            {/* Score Badge */}
            <MatchScore
              score={match.score}
              level={match.level}
              size="medium"
              showLabel={false}
              neonAccent={neonAccent}
            />

            {/* Record Info */}
            <StyledRecordInfo>
              <Typography className="record-title">{recordTitle}</Typography>
              <Box className="record-meta">
                {match.matchedRules.slice(0, 2).map((rule) => (
                  <StyledRuleBadge key={rule} label={rule} size="small" variant="outlined" />
                ))}
                {match.matchedRules.length > 2 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                    }}
                  >
                    +{match.matchedRules.length - 2} more
                  </Typography>
                )}
              </Box>
            </StyledRecordInfo>
          </Box>

          {/* Expand Button */}
          {expandable && (
            <StyledExpandButton $expanded={expanded} onClick={toggleExpanded} size="small">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" />
              </svg>
            </StyledExpandButton>
          )}
        </StyledHeader>
        {/* Field Comparisons */}
        <Collapse in={expanded || !expandable}>
          <StyledContent>
            {showDetails && (
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                {fields.map((field) => (
                  <FieldComparison
                    key={field.name}
                    field={field}
                    sourceValue={sourceRecord.data[field.name]}
                    targetValue={match.record.data[field.name]}
                    score={fieldScoreMap.get(field.name)}
                    highlightDifferences
                    neonAccent={neonAccent}
                  />
                ))}
              </Stack>
            )}

            {/* Action Buttons */}
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                justifyContent: 'flex-end',
              }}
            >
              {onIgnore && (
                <StyledActionButton
                  $variant="ignore"
                  $isDark={isDark}
                  onClick={handleIgnore}
                  disabled={disabled || loading !== null}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading === 'ignore' ? 'Ignoring...' : 'Ignore'}
                </StyledActionButton>
              )}

              {onReject && (
                <StyledActionButton
                  $variant="reject"
                  $isDark={isDark}
                  onClick={handleReject}
                  disabled={disabled || loading !== null}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading === 'reject' ? 'Rejecting...' : 'Reject'}
                </StyledActionButton>
              )}

              {onConfirm && (
                <>
                  <StyledActionButton
                    $variant="confirm"
                    $isDark={isDark}
                    onClick={handleConfirmClick}
                    disabled={disabled || loading !== null}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {loading === 'confirm' ? 'Confirming...' : 'Confirm Match'}
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                      <path d="M6 9l3-3-3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                  </StyledActionButton>

                  <Menu
                    anchorEl={linkMenuAnchor}
                    open={Boolean(linkMenuAnchor)}
                    onClose={() => setLinkMenuAnchor(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    slotProps={{
                      paper: {
                        sx: {
                          mt: 1,
                          borderRadius: 2,
                          minWidth: 200,
                          background: isDark ? 'rgba(30, 41, 59, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                          backdropFilter: 'blur(20px)',
                          border: isDark ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                        },
                      },
                    }}
                  >
                    {LINK_TYPE_OPTIONS.map((option) => (
                      <MenuItem
                        key={option.value}
                        onClick={() => handleLinkTypeSelect(option.value)}
                        sx={{
                          py: 1.5,
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                          }}
                        >
                          {option.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {option.description}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </Stack>
          </StyledContent>
        </Collapse>
      </StyledCard>
    );
  },
);

MatchCard.displayName = 'MatchCard';

export default MatchCard;
